import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ProposalFocusCard } from './cards/ProposalFocusCard';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import type { Proposal } from '../../types/db';
interface ProposalFocusProps {
  pending: Proposal[];
  outcomes: Record<string, 'ok' | 'nee'>;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onEdit: () => void;
  /** Vertical offset from the front card to Anna's core — cards come from her and return into her */
  fromY: number;
  compact: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const PEEK = 40;

/** Anna brings proposals forward one at a time; the next ones wait behind in depth. */
export function ProposalFocus({
  pending,
  outcomes,
  onApprove,
  onReject,
  onEdit,
  fromY,
  compact
}: ProposalFocusProps) {
  const reduce = useMotionPreference();
  const [order, setOrder] = useState<string[]>([]);
  const ids = pending.map(p => p.id);
  const kept = order.filter(id => ids.includes(id));
  const ordered = [...kept, ...ids.filter(id => !kept.includes(id))].map(id => pending.find(p => p.id === id)).filter((p): p is Proposal => Boolean(p));
  const visible = ordered.slice(0, 3);
  const total = ordered.length;
  const later = () => setOrder([...ordered.slice(1), ordered[0]].map(p => p.id));
  return <section aria-label="Wacht op jou" className="relative w-full" style={{
    paddingTop: PEEK * Math.min(2, total - 1)
  }} data-id="element-1520" data-mp="i3mgvjsjxzv8">
      <div className="relative" data-id="element-1521" data-mp="d49uf05leut6">
        <AnimatePresence custom={outcomes} mode="popLayout" initial data-id="element-1522" data-mp="d2yha9q9cape">
          {visible.map((p, i) => {
          const front = i === 0;
          return <motion.div key={p.id} custom={outcomes} aria-hidden={!front} className={front ? 'relative' : 'pointer-events-none absolute inset-0 overflow-hidden rounded-[28px]'} style={{
            zIndex: 10 - i,
            transformOrigin: '50% 0%'
          }} initial={reduce ? {
            opacity: 0
          } : {
            opacity: 0,
            y: fromY,
            scale: 0.25
          }} animate={{
            opacity: 1,
            y: -i * PEEK,
            scale: 1 - i * 0.06,
            filter: front ? 'brightness(1)' : `brightness(${1 - i * 0.25})`,
            transition: reduce ? {
              duration: 0.2
            } : {
              type: 'spring',
              stiffness: 280,
              damping: 30,
              delay: i * 0.05
            }
          }} variants={{
            exit: (o: Record<string, 'ok' | 'nee'>) => reduce ? {
              opacity: 0,
              transition: {
                duration: 0.2
              }
            } : o[p.id] === 'ok' ? {
              opacity: 0,
              y: fromY,
              scale: 0.15,
              transition: {
                duration: 0.3,
                ease: EASE
              }
            } : o[p.id] === 'nee' ? {
              opacity: 0,
              y: 80,
              transition: {
                duration: 0.22,
                ease: EASE
              }
            } : {
              opacity: 0,
              transition: {
                duration: 0.15
              }
            }
          }} exit="exit" data-id="element-1523" data-mp="44msmd8ybmzl" data-mpi={i}>
                <ProposalFocusCard proposal={p} position={i + 1} total={total} compact={compact} onApprove={() => window.setTimeout(() => onApprove(p.id), 450)} onReject={() => onReject(p.id)} onEdit={onEdit} onLater={later} data-id="element-1524" data-mp="2irqhnhm2ewf1" />
              </motion.div>;
        })}
        </AnimatePresence>
      </div>
    </section>;
}